import React, { useState, useContext, useEffect, useRef, useMemo } from 'react';
import useApi from '../Utils/useApi';
import useCoreApi from '../Utils/useCoreApi';
import styles from './InteractiveWindow.module.css';
import { ChatContext } from './ChatContext';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import { MathJax, MathJaxContext } from 'better-react-mathjax';
import he from 'he'; 

const InteractiveWindow = ({ lessonId, userName, lessonData, helpQuestion, handleCloseWindow }) => {
    const { chatLog, addMessageToChatLog, updateMessageInChatLog, clearChatLog } = useContext(ChatContext);
    const [name, setName] = useState('');
    const [age, setAge] = useState(null); // New state for age
    const [isLoading, setIsLoading] = useState(false);
    const [userInput, setUserInput] = useState('');
    const [errorMessage, setErrorMessage] = useState(null);
    const [initialPromptSent, setInitialPromptSent] = useState(false); // New state for initial prompt
    const chatLogEndRef = useRef(null);
    const api = useApi();
    const coreApi = useCoreApi();

    const scrollToBottom = () => {
        chatLogEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };
    useEffect(scrollToBottom, [chatLog]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault(); // Prevent newline
            if (!isLoading && userInput.trim() !== '') {
                handleUserMessage();
            }
        }
    };

    useEffect(() => {
        clearChatLog();
    }, []);

    useEffect(() => {
        const fetchUserName = async () => {
            const userId = localStorage.getItem('user_id');
            const token = localStorage.getItem('accessToken');
            try {
                const userResponse = await api.get(`/user/getname/${userId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const { firstname, lastname, age } = userResponse.data;
                setName(`${firstname} ${lastname}`);
                setAge(age);  // Store the age
            } catch (error) {
                console.error('Error fetching user name:', error);
                setErrorMessage("You must be logged in to access the Classroom.");
            }
        };
    
        fetchUserName();
    }, []);

    useEffect(() => {
        const sendInitialPrompt = async () => {
            if (name && lessonData) {
                const initialMessage = helpQuestion 
                    ? `I need help with this question: ${helpQuestion.question_text}` 
                    : "Hi! I'm ready to learn.";

                try {
                    setIsLoading(true);
                    
                    // Add a placeholder message for streaming and get its index
                    const assistantMessageIndex = addMessageToChatLog({ user: 'assistant', message: '' });
                    
                    const response = await fetch('https://rl-core-app-kpx3b.ondigitalocean.app/tutor/chat', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
                        },
                        body: JSON.stringify({
                            messages: [{ "role": "user", "content": initialMessage }],
                            meta: {
                                lessonId: lessonId,
                                userName: userName,
                                age: age,
                                lessonData: lessonData,
                                helpQuestion: helpQuestion
                            }
                        })
                    });

                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }

                    const reader = response.body.getReader();
                    const decoder = new TextDecoder();
                    let streamedContent = '';

                    while (true) {
                        const { done, value } = await reader.read();
                        if (done) break;
                        
                        const chunk = decoder.decode(value, { stream: true });
                        streamedContent += chunk;
                        
                        // Update the assistant message in real-time
                        updateMessageInChatLog(assistantMessageIndex, { user: 'assistant', message: streamedContent });
                    }
                    
                } catch (error) {
                    console.error('Error sending initial prompt:', error);
                    setErrorMessage('There was a problem communicating with the AI tutor. Please try again later.');
                } finally {
                    setIsLoading(false);
                }
            }
        };

        // Ensure the initial prompt is sent only once
        if (name && lessonData && !initialPromptSent) {
            sendInitialPrompt();
            setInitialPromptSent(true);
        }
    }, [name, lessonData, userName, helpQuestion, age, initialPromptSent]);

    const handleUserMessage = async () => {
        if (userInput.trim() === '') return;
    
        // Calculate indices manually to avoid stale state issues
        const currentLength = chatLog.length;
        const userMessageIndex = currentLength;
        const assistantMessageIndex = currentLength + 1;
        
        console.log('Calculated indices - user:', userMessageIndex, 'assistant:', assistantMessageIndex, 'current chatLog length:', currentLength);
        
        addMessageToChatLog({ user: 'user', message: userInput });
    
        const messages = [
            ...chatLog.map(chat => {
                return { "role": chat.user.toLowerCase(), "content": chat.message }
            }),
            { "role": "user", "content": userInput }
        ];

        try {
            setIsLoading(true);
            setUserInput('');  // clear input immediately
            
            // Add a placeholder message for streaming
            addMessageToChatLog({ user: 'assistant', message: '' });
            
            const response = await fetch('https://rl-core-app-kpx3b.ondigitalocean.app/tutor/chat', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
                },
                body: JSON.stringify({
                    messages,
                    meta: {
                        lessonId: lessonId,
                        userName: userName,
                        age: age,
                        lessonData: lessonData,
                        helpQuestion: helpQuestion
                    }
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            // Check if response is JSON (blocked) or streaming text
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
                // Handle blocked response
                const blockedResponse = await response.json();
                if (blockedResponse.blocked) {
                    updateMessageInChatLog(assistantMessageIndex, { user: 'assistant', message: blockedResponse.result });
                    return;
                }
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let streamedContent = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                
                const chunk = decoder.decode(value, { stream: true });
                streamedContent += chunk;
                
                // Update the assistant message in real-time
                updateMessageInChatLog(assistantMessageIndex, { user: 'assistant', message: streamedContent });
            }
            
            setErrorMessage(null); // Clear any previous error messages
        } catch (error) {
            console.error('Error sending user message:', error);
            setErrorMessage('There was a problem communicating with the AI tutor. Please try again later.');
        } finally {
            setIsLoading(false);
        }
    };

    const sanitizeLaTeX = (message) => {
        // Decode HTML entities
        message = he.decode(message);
        return message;
    };

    const formatMessage = (message) => {
        message = sanitizeLaTeX(message);
    
        const latexRegex = /\$\$([\s\S]*?)\$\$|\$([\s\S]*?)\$/g; // Simplified regex
        const parts = [];
        let lastIndex = 0;
        let match;
    
        while ((match = latexRegex.exec(message)) !== null) {
            // Text before LaTeX
            if (match.index > lastIndex) {
                const textPart = message.substring(lastIndex, match.index);
                parts.push({ type: 'text', content: textPart });
            }
            // LaTeX content
            const latexContent = match[0];
            parts.push({ type: 'math', content: latexContent });
            lastIndex = latexRegex.lastIndex;
        }
    
        // Remaining text after last LaTeX
        if (lastIndex < message.length) {
            const textPart = message.substring(lastIndex);
            parts.push({ type: 'text', content: textPart });
        }
    
        return parts.map((part, idx) => {
            if (part.type === 'math') {
                return (
                    <MathJax key={idx} dynamic>
                        {part.content}
                    </MathJax>
                );
            } else {
                // Handle newlines
                return part.content.split('\n').map((line, lineIdx) => (
                    <p key={`${idx}-${lineIdx}`}>{line}</p>
                ));
            }
        });
    };

    // Memoize formatted messages to prevent unnecessary re-formatting
    const formattedChatLog = useMemo(() => {
        return chatLog.map(message => ({
            ...message,
            formattedMessage: message.user === 'assistant' ? formatMessage(message.message) : message.message
        }));
    }, [chatLog]);
    

    return (
        <MathJaxContext
            version={3}
            config={{
                tex: {
                    inlineMath: [
                        ['$', '$'],
                    ],
                    displayMath: [
                        ['$$', '$$'],
                    ],
                    processEscapes: true,
                },
            }}
        >
            <div className={styles.interactiveWindow}>
                <button onClick={handleCloseWindow} className={styles.closeButton}>Close</button>
                <div className={styles.interactiveWindowContent}>
                    {formattedChatLog.map((message, idx) => (
                        <div key={idx} className={`${styles.messageRow} ${message.user === 'assistant' ? styles.assistantRow : styles.userRow}`}>
                            {message.user === 'assistant' ? (
                                <div className={styles.assistantMessageContainer}>
                                    <div className={styles.assistantAvatar}>
                                        🎓
                                    </div>
                                    <div className={styles.assistantBubble}>
                                        {message.formattedMessage}
                                    </div>
                                </div>
                            ) : (
                                <div className={styles.userMessageContainer}>
                                    <div className={styles.userBubble}>
                                        {message.formattedMessage}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                    {isLoading && (
                        <div className={styles.loadingContainer}>
                            <CircularProgress />
                        </div>
                    )}
                    {errorMessage && ( // Display error messages
                        <div className={styles.errorMessage}>
                            {errorMessage}
                        </div>
                    )}
                    <div ref={chatLogEndRef} />  {/* This is our marker div */}
                </div>

                <div className={styles.inputContainer}>
                    <div className={styles.inputWrapper}>
                        <input
                            value={userInput}
                            onChange={(e) => setUserInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Ask your question..."
                            className={styles.chatInput}
                            disabled={isLoading}
                        />
                        <button 
                            onClick={handleUserMessage}
                            disabled={isLoading || userInput.trim() === ''}
                            className={styles.sendButton}
                            title="Send message"
                        >
                            {isLoading ? '⏳' : '➤'}
                        </button>
                    </div>
                </div>
            </div>
        </MathJaxContext>
    );
};

export default InteractiveWindow;
